import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Loader2, X } from "lucide-react";

// page-image blob cache (object URLs survive re-renders within the visit)
const _thumbCache = new Map();
export async function fetchPageImage(mid, idx) {
  const key = `${mid}/${idx}`;
  if (_thumbCache.has(key)) return _thumbCache.get(key);
  const { data } = await api.get(`/fleet/ts-helper/page-image/${mid}/${idx}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  _thumbCache.set(key, url);
  return url;
}

export function DiagramViewer({ target, onClose }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [meta, setMeta] = useState(null);
  const [sel, setSel] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setImgUrl(null); setMeta(null); setSel(null); setError(null);
    fetchPageImage(target.manual_id, target.page_index)
      .then((u) => live && setImgUrl(u))
      .catch(() => live && setError("Could not load the diagram."));
    api.post("/fleet/ts-helper/diagram", target, { timeout: 180000 })
      .then(({ data }) => live && setMeta(data))
      .catch(() => live && setMeta({ hotspots: [], parts: {} }));
    return () => { live = false; };
  }, [target]);

  const clickable = (meta?.hotspots || []).filter((h) => meta?.parts?.[h.item]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="ts-diagram-viewer">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[94vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-summit-hairline shrink-0">
          <div className="min-w-0">
            <span className="font-display text-sm font-semibold">Diagram</span>
            <span className="text-[11px] text-gray-500 ml-2">
              {meta === null ? "mapping parts…" : clickable.length > 0 ? "tap a highlighted number to see its part" : "no clickable parts found on this page"}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="ts-diagram-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-auto">
          {error && <div className="p-8 text-sm text-red-600 text-center">{error}</div>}
          {!error && !imgUrl && (
            <div className="p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
          )}
          {imgUrl && (
            <div className="relative">
              <img src={imgUrl} alt="diagram" className="w-full block" />
              {clickable.map((h, i) => {
                const w = Math.max((h.x1 - h.x0) * 100, 2.6);
                const ht = Math.max((h.y1 - h.y0) * 100, 2.0);
                const cx = ((h.x0 + h.x1) / 2) * 100;
                const cy = ((h.y0 + h.y1) / 2) * 100;
                return (
                  <button
                    key={i}
                    onClick={() => setSel({ item: h.item, ...meta.parts[h.item] })}
                    className={`absolute rounded-sm border-2 transition-colors ${sel?.item === h.item ? "border-orange-500 bg-orange-400/30" : "border-sky-400/80 bg-sky-300/10 hover:bg-sky-300/30"}`}
                    style={{ left: `${cx - w / 2}%`, top: `${cy - ht / 2}%`, width: `${w}%`, height: `${ht}%` }}
                    data-testid={`ts-hotspot-${h.item}-${i}`}
                    title={`Item ${h.item}`}
                  />
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t border-summit-hairline px-4 py-3 shrink-0 bg-summit-glacier/40 min-h-[58px]" data-testid="ts-part-detail">
          {sel ? (
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
              <span className="font-mono text-xs bg-summit-primary text-white rounded px-1.5 py-0.5">Item {sel.item}</span>
              <span className="font-semibold text-summit-primary">{sel.part_number || "P/N not listed"}</span>
              <span className="text-gray-700">{sel.name}</span>
              {sel.qty && <span className="font-mono text-xs text-gray-500">QTY {sel.qty}</span>}
              {sel.effectivity && <span className="font-mono text-xs text-amber-700">EFF {sel.effectivity}</span>}
            </div>
          ) : meta === null ? (
            <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the parts list…</div>
          ) : (
            <span className="text-xs text-gray-500">{clickable.length > 0 ? "Tap a highlighted callout number on the diagram to show its part number." : "This page has no resolvable callouts."}</span>
          )}
        </div>
      </div>
    </div>
  );
}
